
import { ReactNode } from "react";
import { useLinkedStartup } from "@/hooks/useLinkedStartup";
import { LinkedStartup } from "./LinkedStartup";

interface PostContentProps {
  content: string;
  mediaUrl?: string | null;
  mediaType?: string | null;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentType?: string | null;
}

export const PostContent = ({
  content,
  mediaUrl,
  mediaType,
  attachmentUrl,
  attachmentName,
  attachmentType,
}: PostContentProps) => {
  const { linkedStartup } = useLinkedStartup(content);

  const renderContent = () => {
    const contentWithLinks = addLinksToContent(content);
    return <div className="whitespace-pre-wrap">{contentWithLinks}</div>;
  };

  const addLinksToContent = (text: string): ReactNode[] => {
    const elements: ReactNode[] = [];
    let lastIndex = 0;

    // Regular expressions for different patterns
    const patterns = [
      { regex: /@(\w+)/g, className: "text-[#9b87f5]" },
      { regex: /\$(\w+)/g, className: "text-flux-cyan" },
      { regex: /#(\w+)/g, className: "text-[#9b87f5]" },
      { regex: /(https?:\/\/[^\s]+)/g, className: "text-blue-400 underline" },
    ];

    // Special case for @kickstartagent
    const kickstartAgentRegex = /@kickstartagent\b/gi;
    let kickstartMatch;
    let processedText = text;

    while ((kickstartMatch = kickstartAgentRegex.exec(text)) !== null) {
      const before = processedText.substring(0, kickstartMatch.index);
      const after = processedText.substring(kickstartMatch.index + kickstartMatch[0].length);
      processedText = before + "@KICKSTART_AGENT_PLACEHOLDER" + after;
    }

    // Find all patterns
    for (const pattern of patterns) {
      const matches = [...processedText.matchAll(pattern.regex)];
      
      for (const match of matches) {
        if (match.index !== undefined && match.index > lastIndex) {
          const textBefore = processedText.substring(lastIndex, match.index);
          if (textBefore) {
            const processedBefore = textBefore.replace(/@KICKSTART_AGENT_PLACEHOLDER/g, '@kickstartagent');
            elements.push(processedBefore);
          }
        }

        if (match.index !== undefined) {
          // Special handling for @kickstartagent
          if (match[0].toLowerCase() === '@kickstart_agent_placeholder') {
            elements.push(
              <span key={`kickstart-${match.index}`} className="text-yellow-400 font-medium">
                @kickstartagent
              </span>
            );
          } else {
            elements.push(
              <span key={`${pattern.className}-${match.index}`} className={pattern.className}>
                {match[0]}
              </span>
            );
          }
          lastIndex = match.index + match[0].length;
        }
      }
    }

    if (lastIndex < processedText.length) {
      const remaining = processedText.substring(lastIndex);
      if (remaining) {
        const processedRemaining = remaining.replace(/@KICKSTART_AGENT_PLACEHOLDER/g, '@kickstartagent');
        elements.push(processedRemaining);
      }
    }

    return elements.length > 0 ? elements : [processedText];
  };

  const renderMedia = () => {
    if (!mediaUrl) return null;

    if (mediaType?.startsWith("image/")) {
      return (
        <div className="mt-3 rounded-lg overflow-hidden">
          <img
            src={mediaUrl}
            alt="Post image"
            className="w-full h-auto max-h-[500px] object-cover"
          />
        </div>
      );
    }

    if (mediaType?.startsWith("video/")) {
      return (
        <div className="mt-3 rounded-lg overflow-hidden">
          <video
            src={mediaUrl}
            controls
            className="w-full max-h-[500px]"
          />
        </div>
      );
    }

    return null;
  };

  const renderAttachment = () => {
    if (!attachmentUrl || !attachmentName) return null;

    return (
      <div className="mt-3">
        <a
          href={attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800/70 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-400"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <div className="overflow-hidden">
            <p className="text-sm text-white truncate">{attachmentName}</p>
            <p className="text-xs text-gray-400">
              {attachmentType || "Document"}
            </p>
          </div>
        </a>
      </div>
    );
  };

  return (
    <div>
      {renderContent()}
      {renderMedia()}
      {renderAttachment()}
      {linkedStartup && <LinkedStartup startup={linkedStartup} />}
    </div>
  );
};
